import { useMemo, useState } from 'react'
import type { Npc, Role } from '@shared/types'
import { ApiError, api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import {
  Button, EmptyState, ErrorNote, Field, Input, Modal, Panel, SectionHeading, Textarea,
} from '@/components/ui'

interface Draft {
  id: string | null
  name: string
  role: string
  location: string
  publicNotes: string
  dmSecretNotes: string
}

const BLANK: Draft = { id: null, name: '', role: '', location: '', publicNotes: '', dmSecretNotes: '' }

export function NpcTab({ campaignId, myRole }: { campaignId: string; myRole: Role }) {
  const { data, error, loading, reload } = useAsync(() => api.listNpcs(campaignId), [campaignId])
  const [draft, setDraft] = useState<Draft | null>(null)
  const [query, setQuery] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const isDm = myRole === 'dm'

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return data?.npcs ?? []
    return (data?.npcs ?? []).filter((npc) =>
      [npc.name, npc.role, npc.location, npc.publicNotes, npc.dmSecretNotes ?? '']
        .join(' ').toLowerCase().includes(needle))
  }, [data, query])

  async function save() {
    if (!draft) return
    setActionError(null)
    const payload = {
      name: draft.name.trim(),
      role: draft.role.trim(),
      location: draft.location.trim(),
      publicNotes: draft.publicNotes,
      dmSecretNotes: draft.dmSecretNotes,
    }
    try {
      if (draft.id) await api.updateNpc(campaignId, draft.id, payload)
      else await api.createNpc(campaignId, payload)
      setDraft(null)
      reload()
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not save that NPC')
    }
  }

  return (
    <div className="space-y-5">
      <SectionHeading
        action={
          <div className="flex items-center gap-2">
            {(data?.npcs.length ?? 0) > 0 && (
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                aria-label="Search NPCs"
                className="h-8 w-40 py-0 text-[13px]"
              />
            )}
            {isDm && <Button size="sm" variant="primary" onClick={() => setDraft({ ...BLANK })}>New NPC</Button>}
          </div>
        }
      >
        Who they have met
      </SectionHeading>

      {(error || actionError) && <ErrorNote>{actionError ?? error}</ErrorNote>}

      {loading ? (
        <Panel className="p-6 text-center text-sm text-ink-500">Loading…</Panel>
      ) : !data?.npcs.length ? (
        <EmptyState
          title="No NPCs yet"
          description={isDm
            ? 'Keep a card for everyone the party meets. Public notes are shared; secret notes stay with you.'
            : 'Your DM has not shared any NPCs yet.'}
          action={isDm ? <Button variant="primary" onClick={() => setDraft({ ...BLANK })}>Add the first</Button> : undefined}
        />
      ) : visible.length === 0 ? (
        <Panel className="p-6 text-center text-sm text-ink-500">Nobody matches “{query}”.</Panel>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((npc) => (
            <NpcCard
              key={npc.id}
              npc={npc}
              isDm={isDm}
              onEdit={() => setDraft({
                id: npc.id,
                name: npc.name,
                role: npc.role,
                location: npc.location,
                publicNotes: npc.publicNotes,
                dmSecretNotes: npc.dmSecretNotes ?? '',
              })}
              onDelete={async () => {
                setActionError(null)
                try {
                  await api.deleteNpc(campaignId, npc.id)
                  reload()
                } catch (err) {
                  setActionError(err instanceof ApiError ? err.message : 'Could not delete that')
                }
              }}
            />
          ))}
        </div>
      )}

      <Modal open={draft !== null} title={draft?.id ? 'Edit NPC' : 'New NPC'} onClose={() => setDraft(null)}>
        {draft && (
          <div className="space-y-4">
            <Field label="Name">
              {(id) => (
                <Input id={id} value={draft.name} maxLength={120} autoFocus
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Mister Witch" />
              )}
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Role">
                {(id) => (
                  <Input id={id} value={draft.role} maxLength={120}
                    onChange={(e) => setDraft({ ...draft, role: e.target.value })} placeholder="Carnival proprietor" />
                )}
              </Field>
              <Field label="Where">
                {(id) => (
                  <Input id={id} value={draft.location} maxLength={120}
                    onChange={(e) => setDraft({ ...draft, location: e.target.value })} placeholder="Witchlight Carnival" />
                )}
              </Field>
            </div>
            <Field label="Public notes" hint="Players can read this.">
              {(id) => (
                <Textarea id={id} value={draft.publicNotes} maxLength={20000}
                  onChange={(e) => setDraft({ ...draft, publicNotes: e.target.value })}
                  placeholder="Impeccably polite. Never blinks." />
              )}
            </Field>
            <Field label="Secret notes" hint="Only you ever see this — it is never sent to a player's browser.">
              {(id) => (
                <Textarea id={id} value={draft.dmSecretNotes} maxLength={20000}
                  onChange={(e) => setDraft({ ...draft, dmSecretNotes: e.target.value })}
                  placeholder="Actually bound to the Hourglass Coven. Will betray them at the Palace." />
              )}
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
              <Button variant="primary" disabled={!draft.name.trim()} onClick={save}>Save NPC</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

function NpcCard({
  npc, isDm, onEdit, onDelete,
}: {
  npc: Npc
  isDm: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const [showSecret, setShowSecret] = useState(false)
  const hasSecret = Boolean(npc.dmSecretNotes?.trim())

  return (
    <Panel className="flex h-full flex-col p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate font-display text-base leading-tight">{npc.name}</h3>
          {(npc.role || npc.location) && (
            <p className="mt-0.5 truncate text-xs text-ink-400">
              {[npc.role, npc.location].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        {isDm && (
          <div className="flex shrink-0 gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit} aria-label={`Edit ${npc.name}`}>Edit</Button>
            <Button variant="ghost" size="sm" onClick={onDelete} aria-label={`Delete ${npc.name}`}>✕</Button>
          </div>
        )}
      </div>

      {npc.publicNotes && (
        <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-ink-300">{npc.publicNotes}</p>
      )}

      {isDm && hasSecret && (
        <div className="mt-auto pt-3">
          <button
            type="button"
            onClick={() => setShowSecret((value) => !value)}
            aria-expanded={showSecret}
            className="flex w-full items-center gap-1.5 rounded-lg border border-arcane-500/30 bg-arcane-900/30 px-2.5 py-1.5 text-left text-[11px] font-medium uppercase tracking-wider text-arcane-400 transition-colors hover:bg-arcane-900/60"
          >
            <span aria-hidden>{showSecret ? '▾' : '▸'}</span>
            Your notes
          </button>
          {showSecret && (
            <p className="mt-2 whitespace-pre-wrap rounded-lg bg-ink-900/60 p-2.5 text-sm leading-relaxed text-arcane-400">
              {npc.dmSecretNotes}
            </p>
          )}
        </div>
      )}

      {!npc.publicNotes && (
        <p className="mt-3 text-sm text-ink-500">
          {isDm ? 'No public notes yet.' : 'No notes shared yet.'}
        </p>
      )}
    </Panel>
  )
}
